import { type MouseEvent } from "react"
import {
    Stack,
    ToggleButton,
    ToggleButtonOption,
    ToggleButtonOptionGroup,
    type ToggleValue,
    Typography,
} from "@octopus-energy/coral/atoms"
import { useTranslations } from "next-intl"
import { getOnboardingConfig } from "@/features/subscription"
import { type JourneyType } from "@/scalars/journeyType"
import { useInitialOnboardingFormContext } from "../../context/InitialOnboardingFormContext"

const isSubscriptionJourneyType = (value: ToggleValue | null): value is JourneyType =>
    typeof value === "string" && ["CHANGE_OF_SUPPLIER", "COMMISSIONING"].includes(value)

export function JourneyTypeToggle() {
    const t = useTranslations("JourneyTypeToggle")
    const { journeyType, onboardingState, updateJourneyType } = useInitialOnboardingFormContext()

    const { availableJourneyTypes } = getOnboardingConfig({
        salesChannel: onboardingState.formValues.affiliateOrganisationSalesChannel,
        affiliateCode: onboardingState.formValues.affiliateCode,
        affiliateOrganisation: onboardingState.formValues.affiliateOrganisationName,
    })

    if (
        !availableJourneyTypes ||
        availableJourneyTypes.length < 2 ||
        onboardingState.formValues.subscriptionType === "MOVE_OUT"
    ) {
        return null
    }

    const journeyTypesValues = availableJourneyTypes.map((jt) => ({
        value: jt,
        label: jt === "CHANGE_OF_SUPPLIER" ? t("toggle.cdf.label") : t("toggle.mes.label"),
        subLabel: jt === "CHANGE_OF_SUPPLIER" ? t("toggle.cdf.subLabel") : t("toggle.mes.subLabel"),
        attributes: { "data-testid": `set${jt}Button` },
    }))

    const handleJourneyTypeChange = (_: MouseEvent, value: ToggleValue | null) => {
        if (isSubscriptionJourneyType(value)) {
            updateJourneyType(value)
        }
    }

    return (
        <>
            <Typography textStyle="h4">{t("label")}</Typography>
            <ToggleButton
                fullWidth
                defaultValue={journeyType}
                onChange={handleJourneyTypeChange}
            >
                <ToggleButtonOptionGroup>
                    <Stack gap="xxs">
                        {journeyTypesValues.map((option) => (
                            <ToggleButtonOption
                                key={option.value}
                                value={option.value}
                                attributes={option.attributes}
                            >
                                <Stack
                                    flexDirection="column"
                                    gap="none"
                                >
                                    <Typography
                                        component="p"
                                        textStyle={{ base: "body2", sm: "body1" }}
                                    >
                                        <b>{option.label}</b>
                                    </Typography>
                                    <Typography textStyle="caption">{option.subLabel}</Typography>
                                </Stack>
                            </ToggleButtonOption>
                        ))}
                    </Stack>
                </ToggleButtonOptionGroup>
            </ToggleButton>
        </>
    )
}

JourneyTypeToggle.messages = ["JourneyTypeToggle"] as const
